<template>
  <div :class="['advanced-organization-select', className, `size-${size}`]" >
    <span class="advanced-organization-select__title" v-if="title">{{ title }}</span>
    <v-autocomplete
      v-model="organizationSelected"
      :items="itemsSorted"
      :size="size"
      :cacheItems='false'
      dense
      append-icon="expand_more"
      outlined
      multiple
      height="40"
      :filter="customFilter"
      :search-input.sync="filterSearch"
      :placeholder="organizationSelected.length > 0 ? '' : $t('advanced.organization.select.do.select')"
      :menu-props="{contentClass:'advanced-organization-select__content'}"
      @focus="onFocus"
    >
      <template v-slot:append-outer>
        <custom-tooltip class='tool-tip-hint' placement="right" popper-class="light-tooltip">
          <div slot='buttonContent'>
          <span class="tag" :class='[searchByTag ? "material-icons" : "material-icons-outlined"]' @click.stop='toggleSearchByTag'>
            local_offer
          </span>
          </div>
          <div slot="content">
            <slot name="tooltip">
              <span class='tooltip-text'>{{ $t('advanced.domain.search.by.tag.tool.tip') }}</span>
            </slot>
          </div>
        </custom-tooltip>
      </template>
      <template v-slot:selection="{ index }">
        <div v-if="selectAll && index === 0">
          <span v-if="organizationSelected.length === 1" class="selection-text">{{ organizationSelected[0] }}</span>
          <span v-else class="selection-text">{{ $t('advanced.organization.select.selection.all') }}</span>
        </div>
        <div v-else-if="selectSome && index === 0">
          <span v-if="organizationSelected.length === 1" class="selection-text">{{ organizationSelected[0] }}</span>
          <span v-else class="selection-text">{{ $t('advanced.domain.select.selection.selected')
            }} {{ organizationSelected.length }} {{ $t('advanced.organization.select.selection') }}</span>
        </div>
      </template>
      <template v-slot:prepend-item>
        <div class='list-actions'>
          <v-list-item
            ripple
            @click="toggle"
          >
            <v-list-item-action>
              <v-icon class="material-icons-outlined" :color="organizationSelected.length > 0 ? purpleColor : ''">
                {{ icon }}
              </v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ $t('advanced.domain.select.all') }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </div>
        <v-divider class="mt-2"></v-divider>
      </template>
      <template v-slot:item="data">
        <v-list-item
          v-bind="data.attrs"
          ripple
          @click="toggleSingle(data.item)"
          :disabled="isOrganizationDisabled(data.item)"
        >
          <v-list-item-content>
            <div class='domain-item-row'>
              <v-icon v-if='organizationSelected.includes(data.item)' class="material-icons-outlined" :color="organizationSelected.includes(data.item) ? purpleColor : ''">
                check_box
              </v-icon>
              <v-icon v-else class="material-icons-outlined" :class='{"disabled": isOrganizationDisabled(data.item)}' :color="organizationSelected.includes(data.item) ? purpleColor : ''">
                check_box_outline_blank
              </v-icon>
              <v-avatar size="28" :class='{"icon": !getLogo(data.item), "disabled": isOrganizationDisabled(data.item)}'>
                <img v-if="getLogo(data.item)"
                     class="avatar" :src="getLogo(data.item)" alt=''>
                <v-icon v-else> business </v-icon>
              </v-avatar>
              <div class='domain-item-row__details'>
                <v-list-item-title class='org-name'>
                  {{ data.item }}
                  <span
                    v-if="nameToFullOrganization[data.item] && nameToFullOrganization[data.item].isSupplyChain"
                    class="supply-chain-badge"
                  >Supply Chain</span>
                </v-list-item-title>
                <v-list-item-subtitle class='assets-count'>
                  {{ getSubTitle(data.item) }}
                </v-list-item-subtitle>
              </div>
            </div>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template v-slot:append-item>
        <v-divider class="mb-2"></v-divider>
        <v-list-item disabled>
          <v-list-item-avatar :color="isDarkMode ? 'grey darken-3' : 'grey lighten-3'">
            <v-icon :color="isDarkMode ? 'grey lighten-1' : ''">
              apartment
            </v-icon>
          </v-list-item-avatar>

          <v-list-item-content v-if="selectAll">
            <v-list-item-title>
              {{ $t('advanced.organization.select.all.selected') }}
            </v-list-item-title>
          </v-list-item-content>

          <v-list-item-content v-else-if="selectSome">
            <v-list-item-title>
              {{ $t('advanced.organization.select.total') }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ organizationSelected.length }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-content v-else>
            <v-list-item-title>
              {{ $t('advanced.organization.select.no.selected') }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ $t('advanced.organization.select.do.select') }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>

import { mapGetters } from 'vuex';
import { SET_ORGANIZATION_FILTER } from '@/store/filters/actionTypes';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import {
  CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS,
  LOAD_DOMAINS_FROM_CONFIGURATION,
} from '@/store/project/actionTypes';

export default {
  name: 'AdvancedOrganizationSelect',
  props: {
    className: {
      type: String,
      default: () => '',
    },
    title: {
      type: String,
      default: () => '',
    },
    size: {
      type: String,
      default: () => '',
    },
  },
  components: {
    CustomTooltip,
  },
  data() {
    return {
      filterSearch: '',
      searchByTag: false,
      organizationSelectedSorted: [],
    };
  },
  computed: {
    ...mapGetters([
      'organizationFilter',
      'organizationsTags',
      'organizations',
      'domainsPerOrganization',
      'configurationCloudAccounts',
      'paginationItemsPerPageByTabs',
      'domainFilter',
    ]),
    ...mapGetters('theme', ['isDarkMode']),
    purpleColor() {
      return this.isDarkMode ? '#D499F5' : '#8E30D0';
    },
    organizationSelected: {
      async set(val) {
        await this.$store.dispatch(SET_ORGANIZATION_FILTER, {organizations: val, organizationFilter: this.organizationFilter, domainFilter: this.domainFilter});
        await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
        if (this.organizationFilter.length) {
          await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
            sort: {
              configData: 'DESC',
            },
            page: 1,
            limit: this.paginationItemsPerPageByTabs.cloudAccount,
          });
        }
        this.filterSearch = '';
      },
      get() {
        return this.organizationFilter;
      },
    },
    items() {
      return [...this.organizations].map(item => item.name);
    },
    itemsSorted() {
      const itemsCopy = [...this.items].sort(this.organizationCompare);
      if (this.organizationSelectedSorted.length) {
        const selectedCopy = [...this.organizationSelectedSorted].sort(this.organizationCompare);
        return [...new Set(selectedCopy.concat(itemsCopy))];
      }
      return itemsCopy;
    },
    nameToFullOrganization() {
      const nameToFullOrganization = {};
      this.organizations.forEach(org => {
        nameToFullOrganization[org.name] = org;
      });
      return nameToFullOrganization;
    },
    selectAll() {
      return this.organizationSelected.length === this.itemsSorted.filter(org => !this.isOrganizationDisabled(org)).length;
    },
    selectSome() {
      return this.organizationSelected.length > 0 && !this.selectAll;
    },
    selectAllFromFilter() {
      return this.itemsByFilter.every(item => this.organizationSelected.includes(item));
    },
    itemsByFilter() {
      return !this.filterSearch ? this.itemsSorted : this.itemsSorted.filter(item => this.customFilter(item, this.filterSearch, item));
    },
    icon() {
      if (this.selectAll || this.selectAllFromFilter) return 'disabled_by_default';
      if (this.selectSome) return 'indeterminate_check_box';
      return 'check_box_outline_blank';
    },
  },
  methods: {
    getLogo(organizationName) {
      return this.nameToFullOrganization[organizationName].logoURL || null;
    },
    getSubTitle(organizationName) {
      return `${this.getDomainsCount(organizationName) + this.getOrganizationsCount(organizationName)} ${this.$t('assets')}`;
    },
    isOrganizationDisabled(organizationName) {
      return this.getDomainsCount(organizationName) === 0 && this.getOrganizationsCount(organizationName) === 0;
    },
    getDomainsCount(organizationName) {
      if (this.domainsPerOrganization.has(organizationName)) {
        return this.domainsPerOrganization.get(organizationName).length;
      }
      return 0;
    },
    getOrganizationsCount(organizationName) {
      return this.configurationCloudAccounts.filter(cloudAccount => organizationName === cloudAccount.organization.name).length;
    },
    toggleSearchByTag() {
      this.searchByTag = !this.searchByTag;
    },
    applyMouseEnter() {
      this.$refs.applyAnimation.play();
    },
    loopComplete() {
      this.$refs.applyAnimation.pause();
    },
    organizationCompare(a, b) {
      if (a === b) return 0;
      const aIsSupplyChain = this.nameToFullOrganization[a]?.isSupplyChain;
      const bIsSupplyChain = this.nameToFullOrganization[b]?.isSupplyChain;
      if (aIsSupplyChain && !bIsSupplyChain) return -1;
      if (bIsSupplyChain && !aIsSupplyChain) return 1;
      if (this.isOrganizationDisabled(a) && !this.isOrganizationDisabled(b)) {
        return 1;
      }
      if (this.isOrganizationDisabled(b) && !this.isOrganizationDisabled(a)) {
        return -1;
      }
      return a > b ? 1 : -1;
    },
    // eslint-disable-next-line no-unused-vars
    customFilter(item, queryText, itemText) {
      if (queryText.length < 1) {
        return true;
      }
      if (this.searchByTag) {
        return Object.keys(this.organizationsTags[item])
          .some(tag => this.organizationsTags[item][tag] === 'added' && tag.toLocaleLowerCase().indexOf(queryText.toLowerCase()) > -1);
      }
      return itemText.toLocaleLowerCase().indexOf(queryText.toLowerCase()) > -1;
    },
    toggle() {
      if (this.selectAll) {
        this.organizationSelected = [];
      } else {
        this.organizationSelected = Array.from(new Set([...this.organizationSelected, ...this.itemsByFilter])).filter(org => !this.isOrganizationDisabled(org));
      }
    },
    onFocus() {
      this.organizationSelectedSorted = Array.from(new Set(this.organizationSelectedSorted.concat(this.organizationSelected)));
    },
    toggleSingle(item) {
      if (this.organizationSelected.includes(item)) {
        this.organizationSelected = this.organizationSelected.filter(org => org !== item);
        this.organizationSelectedSorted = this.organizationSelectedSorted.filter(org => org !== item);
      } else {
        this.organizationSelected = [...this.organizationSelected, item];
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/design-system';

.tag {
  color: var(--text-muted);
  font-size: $font-size-xl;

  &:hover {
    cursor: pointer;
  }
}

.tool-tip-hint {
  display: flex;
  align-items: center;
}

img.avatar {
  width: rem(28)!important;
  height: rem(28)!important;
}

.domain-item-row {
  display: flex;
  align-items: center;

  .v-avatar.v-avatar {
    min-width: rem(28);
    width: rem(28)!important;
    height: rem(28)!important;
    border: 1px solid var(--brand-primary);

    &.icon {
      border: none;

      .v-icon {
        color: var(--text-secondary);
      }
    }

    &.disabled {
      .v-icon {
        color: var(--text-muted);
      }
    }
  }

  .material-icons-outlined {
    margin-right: $space-2;
    color: var(--checkbox-icon);

    &.disabled {
      color: var(--text-muted);
    }
  }

  &__details {
    margin-left: 2%;
    display: flex;
    flex-direction: column;

    .org-name.org-name {
      align-self: start;
      display: flex;
      align-items: center;
      gap: $space-1-5;
    }

    .supply-chain-badge {
      display: inline-block;
      padding: rem(1) rem(6);
      border-radius: rem(10);
      background: rgba(139, 92, 246, 0.12);
      border: 1px solid rgba(139, 92, 246, 0.4);
      color: #7c3aed;
      font-size: rem(10);
      font-family: $font-family-bold;
      white-space: nowrap;
    }

    .assets-count {
      font-size: $font-size-xs;
    }
  }
}

.v-autocomplete__content.v-menu__content .v-select-list {
  position: relative;
}

.list-actions {
  display: flex;
  top: 0;
  position: sticky;
  background-color: var(--bg-secondary);
  z-index: 1;
}

.advanced-organization-select {
  :deep(.v-input) {
    margin-top: 0;
  }

  &__title {
    font-family: $font-family-regular;
    font-size: $font-size-xs;
    letter-spacing: 0.8px;
    line-height: $font-size-xs;
    margin-bottom: $space-1;
    color: var(--text-primary);
  }
}

.selection-text {
  color: var(--text-primary);
  font-family: $font-family-regular;
  font-size: $font-size-base;
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.advanced-organization-select {
  position: relative;

  :deep(.theme--light.v-list-item:not(.v-list-item--disabled) .v-list-item__subtitle) {
    margin-left: 14%;
  }

  // Override Vuetify default margin to position tag icon centered on the input border
  .v-input__append-outer {
    position: absolute;
    right: $space-8;
    top: 50%;
    transform: translateY(-50%);
    margin: 0 !important;
    width: auto;
    display: flex;
    align-items: center;
  }

  // Override Vuetify default margin to vertically center chevron within the input
  .v-input__icon--append {
    display: flex;
    align-items: center;
    margin-top: $space-1 !important;

    .v-icon {
      color: var(--text-muted);
    }
  }
  .v-input__control {
    display: flex;
    align-items: center;
  }

  &.background {
    min-width: rem(285);
    max-width: rem(285);
    margin-top: $space-1;

    .v-select__slot {
      background-color: transparent;
    }
  }

  .v-select__selections {
    margin-left: $space-1;
    padding-right: $space-6;
    flex-wrap: nowrap;

    :first-child {
      max-width: rem(180);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      .selection-text {
        margin-left: $space-2;
      }
    }

    input {
      margin-right: $space-6;
    }
  }

  .v-select__slot {
    background-color: transparent;
  }

  // Override vuetify-overrides outlined input background
  .v-autocomplete.v-autocomplete.v-text-field--outlined .v-input__slot {
    background-color: var(--bg-primary);
  }

  .v-text-field--outlined input {
    padding: 0;
  }
}

.advanced-organization-select__content {
  min-width: rem(285);
  max-width: rem(285);

  .v-list.v-list {
    background: var(--bg-primary);
  }

  .list-actions {
    background: var(--bg-primary);
  }

  // Fix selected row highlight - lighter purple background
  .v-list-item.v-list-item--highlighted,
  .v-list-item.v-list-item--active {
    background-color: rgba(143, 36, 211, 0.1);
  }

  // When row is selected/highlighted, organization name should be purple
  .v-list-item--highlighted .v-list-item__content .domain-item-row .org-name,
  .v-list-item--active .v-list-item__content .domain-item-row .org-name {
    color: var(--brand-primary);
  }

  // Icons should also be purple when selected
  .v-list-item--highlighted .v-list-item__content .domain-item-row .v-icon,
  .v-list-item--active .v-list-item__content .domain-item-row .v-icon {
    color: var(--brand-primary);
  }
}
</style>
